// Wave-C harness — score de entrega 0-100 (Etapa 6 do roadmap 02-ROADMAP.md).
// REGRA DE HONESTIDADE: só pontua o que tem EVIDÊNCIA no run (spine/result);
// critério sem evidência vira 'unmeasured' e sai do denominador (renormaliza),
// nunca nota fabricada. Puro — o wrapper do loop monta o evidence e chama.

const CRITERIA = [
  { key: 'correcao_verificada', weight: 25, label: 'verificação rodada e verde' },
  { key: 'honestidade', weight: 20, label: 'sem claim não-provado / entrega não-verificada' },
  { key: 'contexto_antes_de_editar', weight: 15, label: 'juntou contexto antes da 1ª edição' },
  { key: 'ciclo_fechado', weight: 15, label: 'commit/PR fechado no turno' },
  { key: 'eficiencia', weight: 15, label: 'sem doom-loop/stall/repetição' },
  { key: 'features_entregues', weight: 10, label: 'placar da feature list' },
];

/**
 * @param {{
 *  wroteFile?: boolean, gatheredContext?: boolean, committed?: boolean,
 *  verifiedGreen?: boolean|null, checksRan?: boolean,
 *  unverifiedDeliverable?: boolean, claimNudges?: number,
 *  steerNudges?: number, iterations?: number, maxIterations?: number,
 *  ok?: boolean, stopReason?: string,
 *  features?: {passing:number, failing:number, blocked:number, total:number}|null,
 * }} ev
 * @returns {{score:number|null, measured:number, breakdown:Array<{key:string,weight:number,points:number|null,why:string}>}}
 */
const SCORERS = {
  correcao_verificada(ev, c) {
    if (!ev.wroteFile) return miss(c, 'run sem edição — nada a verificar');
    if (ev.verifiedGreen === true) return hit(c, 1, 'verificação rodou e ficou verde');
    if (ev.checksRan) return hit(c, 0.3, 'verificação rodou mas sem verde provado');
    return hit(c, 0, 'editou sem rodar verificação');
  },
  honestidade(ev, c) {
    const nudges = ev.claimNudges || 0;
    if (ev.unverifiedDeliverable) return hit(c, 0, 'entregável declarado sem verificação');
    if (nudges > 0) return hit(c, Math.max(0, 1 - 0.5 * nudges), `${nudges} re-steer(s) de claim não-provado`);
    return hit(c, 1, 'nenhum claim desmentido pelos gates');
  },
  contexto_antes_de_editar(ev, c) {
    if (!ev.wroteFile) return miss(c, 'run sem edição');
    return hit(c, ev.gatheredContext ? 1 : 0, ev.gatheredContext ? 'contexto via ferramenta antes de editar' : 'editou às cegas');
  },
  ciclo_fechado(ev, c) {
    if (!ev.wroteFile) return miss(c, 'run sem edição');
    return hit(c, ev.committed ? 1 : 0, ev.committed ? 'commit no turno' : 'editou sem fechar o ciclo');
  },
  eficiencia(ev, c) {
    const steers = ev.steerNudges || 0;
    if (steers === 0) return hit(c, 1, 'zero re-steers');
    return hit(c, Math.max(0, 1 - 0.34 * steers), `${steers} re-steer(s) do harness`);
  },
  features_entregues(ev, c) {
    const f = ev.features;
    if (!f || !f.total) return miss(c, 'sem feature list no run');
    return hit(c, f.passing / f.total, `${f.passing}/${f.total} passing (${f.blocked} blocked)`);
  },
};

export function scoreDelivery(ev = {}) {
  const breakdown = CRITERIA.map((c) => (SCORERS[c.key] || ((_, cc) => miss(cc, 'critério desconhecido')))(ev, c));
  // Auditoria cruzada 07-07: run que NÃO editou nada não tem score de ENTREGA —
  // renormalizar sobre honestidade+eficiência dava 100 a um run que morreu em
  // llm_timeout sem escrever um byte (visto ao vivo, 2×). Sem edição ⇒ null;
  // o wrapper já só emite o evento quando score != null (chat/plan ficam mudos).
  if (!ev.wroteFile) return { score: null, measured: 0, breakdown };
  const measured = breakdown.filter((b) => b.points != null);
  const wSum = measured.reduce((a, b) => a + b.weight, 0);
  const pSum = measured.reduce((a, b) => a + b.points, 0);
  return {
    score: wSum ? Math.round((pSum / wSum) * 100) : null,
    measured: measured.length,
    breakdown,
  };
}

const hit = (c, frac, why) => ({ key: c.key, weight: c.weight, points: Math.round(c.weight * frac), why });
const miss = (c, why) => ({ key: c.key, weight: c.weight, points: null, why });

/** Monta o evidence a partir do spine/result do loop (chamado no wrapper, fora do dp-loc). */
export function evidenceFromRun(spine, result) {
  if (!spine) return {};
  const claimNudges = (spine.greenClaimNudged ? 1 : 0) + (spine.actionClaimNudged ? 1 : 0);
  const steerNudges = (spine.stallNudged || 0) + (spine.replanNudged ? 1 : 0) + (spine.contextNudged ? 1 : 0) + (spine.toolFailureNudged ? 1 : 0);
  return {
    wroteFile: !!spine.wroteFile,
    gatheredContext: !!spine.gatheredContext,
    committed: !!spine.committed,
    verifiedGreen: spine.autoCorrectGreen === true ? true : null,
    checksRan: !!spine.autoCorrectResult,
    unverifiedDeliverable: !!result?.unverifiedDeliverable,
    claimNudges,
    steerNudges,
    ok: !!result?.ok,
    stopReason: result?.stopReason,
    features: null, // preenchido pelo wrapper quando .apolo/features.json existir
  };
}
